/**
 * AKS Management — Lens-style Kubernetes cluster management.
 * Full workload / config / network / storage resource browser with
 * detail panel, logs viewer, and simulated shell.
 */
import { useState, useRef, useEffect } from 'react'
import {
  Server, RefreshCw, ChevronDown, ChevronRight,
  AlertCircle, Terminal, Layers, Globe,
  Box, Settings, Database, Network, HardDrive, GitBranch,
  Search, X, Copy, ExternalLink, Zap, Code, List,
  Tag, Calendar, AlertTriangle, Info,
  Shield, Users, Key, UserCheck,
} from 'lucide-react'
import clsx from 'clsx'
import styles from './AKSManagementPage.module.css'

// ─── Types ────────────────────────────────────────────────────────────────────

type ResourceKind =
  | 'overview' | 'nodes' | 'namespaces' | 'events'
  | 'pods' | 'deployments' | 'daemonsets' | 'statefulsets' | 'replicasets' | 'jobs' | 'cronjobs'
  | 'configmaps' | 'secrets' | 'hpas' | 'resourcequotas'
  | 'services' | 'endpoints' | 'ingresses'
  | 'pvcs' | 'pvs' | 'storageclasses'
  | 'releases'
  | 'roles' | 'clusterroles' | 'rolebindings' | 'clusterrolebindings' | 'serviceaccounts'

type PodPhase    = 'Running' | 'Pending' | 'Succeeded' | 'Failed' | 'CrashLoopBackOff' | 'Terminating' | 'Evicted'
type NodeCond    = 'Ready' | 'NotReady' | 'SchedulingDisabled'
type SvcType     = 'ClusterIP' | 'NodePort' | 'LoadBalancer' | 'ExternalName'
type PVCState    = 'Bound' | 'Pending' | 'Lost'
type HelmStatus  = 'deployed' | 'failed' | 'pending-upgrade' | 'superseded'

interface KubeResource {
  uid: string
  name: string
  namespace: string
  age: string
  labels: Record<string, string>
}

interface KubePod extends KubeResource {
  phase: PodPhase
  ready: string      // e.g. "2/2"
  restarts: number
  node: string
  ip: string
  containers: string[]
  image: string
}

interface KubeDeployment extends KubeResource {
  ready: string
  upToDate: number
  available: number
  image: string
}

interface KubeDaemonSet extends KubeResource {
  desired: number
  current: number
  ready: number
  image: string
}

interface KubeStatefulSet extends KubeResource {
  ready: string
  image: string
}

interface KubeJob extends KubeResource {
  completions: string
  duration: string
  status: 'Complete' | 'Running' | 'Failed'
}

interface KubeCronJob extends KubeResource {
  schedule: string
  lastRun: string
  active: number
  suspended: boolean
}

interface KubeNode {
  uid: string
  name: string
  status: NodeCond
  roles: string[]
  version: string
  age: string
  os: string
  arch: string
  cpu: string
  memory: string
  cpuUsed: number
  memUsed: number
  pool: string
}

interface KubeNamespace {
  uid: string
  name: string
  status: 'Active' | 'Terminating'
  age: string
  labels: Record<string, string>
}

interface KubeConfigMap extends KubeResource { keys: number }
interface KubeSecret extends KubeResource { type: string; keys: number }
interface KubeHPA extends KubeResource {
  ref: string; minPods: number; maxPods: number
  currentPods: number; targetCPU: string; currentCPU: string
}

interface KubeService extends KubeResource {
  type: SvcType; clusterIP: string; externalIP: string
  ports: string; selector: Record<string, string>
}

interface KubeIngress extends KubeResource {
  className: string; hosts: string[]; address: string
}

interface KubePVC extends KubeResource {
  status: PVCState; storageClass: string; capacity: string; accessMode: string
}

interface KubePV {
  uid: string; name: string; capacity: string; accessMode: string
  reclaim: string; status: PVCState; storageClass: string; age: string
  claim: string
}

interface KubeStorageClass {
  uid: string; name: string; provisioner: string; reclaimPolicy: string
  volumeBinding: string; default: boolean; age: string
}

interface KubeEvent {
  uid: string; namespace: string; name: string; reason: string
  message: string; type: 'Normal' | 'Warning'; count: number; age: string
  object: string
}

interface HelmRelease {
  uid: string; name: string; namespace: string; chart: string
  chartVersion: string; appVersion: string; status: HelmStatus; updated: string
}

interface PolicyRule {
  verbs: string[]
  apiGroups: string[]
  resources: string[]
}

interface KubeRole extends KubeResource {
  rules: PolicyRule[]
  ruleCount: number
}

interface KubeClusterRole {
  uid: string; name: string; age: string
  rules: PolicyRule[]
  ruleCount: number
  aggregationRule: boolean
  builtIn: boolean
}

interface KubeRoleBinding extends KubeResource {
  roleRef: string
  roleKind: 'Role' | 'ClusterRole'
  subjects: Array<{ kind: string; name: string; namespace?: string }>
}

interface KubeClusterRoleBinding {
  uid: string; name: string; age: string
  roleRef: string
  subjects: Array<{ kind: string; name: string; namespace?: string }>
  builtIn: boolean
}

interface KubeServiceAccount extends KubeResource {
  secrets: number
  automountToken: boolean
}

interface AKSCluster {
  id: string; name: string; resourceGroup: string; region: string
  k8sVersion: string; status: 'Running' | 'Stopped' | 'Failed' | 'Upgrading'
  environment: string; tenantName: string
  apiServerUrl: string; createdAt: string; lastActivity: string
}

// ─── Mock Data ────────────────────────────────────────────────────────────────

const CLUSTERS: AKSCluster[] = [
  { id: 'c1', name: 'aks-acme-prod-eus',    resourceGroup: 'rg-acme-prod-eastus',  region: 'East US',     k8sVersion: '1.29.4', status: 'Running',   environment: 'production', tenantName: 'Acme Corp',        apiServerUrl: 'https://aks-acme-prod-eus-abc123.hcp.eastus.azmk8s.io:443',          createdAt: '2025-11-14', lastActivity: '2m ago' },
  { id: 'c2', name: 'aks-globex-uat-weu',   resourceGroup: 'rg-globex-uat-westeu', region: 'West Europe', k8sVersion: '1.28.9', status: 'Running',   environment: 'uat',        tenantName: 'Globex Industries', apiServerUrl: 'https://aks-globex-uat-weu-def456.hcp.westeurope.azmk8s.io:443',  createdAt: '2025-12-01', lastActivity: '2h ago' },
  { id: 'c3', name: 'aks-initech-dev-sea',  resourceGroup: 'rg-initech-dev-sea',   region: 'SE Asia',     k8sVersion: '1.29.4', status: 'Stopped',   environment: 'dev',        tenantName: 'Initech LLC',       apiServerUrl: 'https://aks-initech-dev-sea-ghi789.hcp.southeastasia.azmk8s.io:443', createdAt: '2026-01-10', lastActivity: '3d ago' },
  { id: 'c4', name: 'aks-hooli-prod-wus2',  resourceGroup: 'rg-hooli-prod-westus2',region: 'West US 2',   k8sVersion: '1.29.4', status: 'Upgrading', environment: 'production', tenantName: 'Hooli Corp',        apiServerUrl: 'https://aks-hooli-prod-wus2-jkl012.hcp.westus2.azmk8s.io:443',     createdAt: '2025-10-05', lastActivity: '10m ago' },
]

const NODES: KubeNode[] = [
  { uid:'n1', name:'aks-system-12345678-0',  status:'Ready', roles:['control-plane'], version:'v1.29.4', age:'85d', os:'Ubuntu 22.04', arch:'amd64', cpu:'4 cores', memory:'16Gi', cpuUsed:38, memUsed:52, pool:'system'  },
  { uid:'n2', name:'aks-system-12345678-1',  status:'Ready', roles:['control-plane'], version:'v1.29.4', age:'85d', os:'Ubuntu 22.04', arch:'amd64', cpu:'4 cores', memory:'16Gi', cpuUsed:41, memUsed:56, pool:'system'  },
  { uid:'n3', name:'aks-system-12345678-2',  status:'Ready', roles:['control-plane'], version:'v1.29.4', age:'85d', os:'Ubuntu 22.04', arch:'amd64', cpu:'4 cores', memory:'16Gi', cpuUsed:35, memUsed:48, pool:'system'  },
  { uid:'n4', name:'aks-apppool-87654321-0', status:'Ready', roles:['worker'],        version:'v1.29.4', age:'60d', os:'Ubuntu 22.04', arch:'amd64', cpu:'8 cores', memory:'32Gi', cpuUsed:62, memUsed:71, pool:'apppool' },
  { uid:'n5', name:'aks-apppool-87654321-1', status:'Ready', roles:['worker'],        version:'v1.29.4', age:'60d', os:'Ubuntu 22.04', arch:'amd64', cpu:'8 cores', memory:'32Gi', cpuUsed:58, memUsed:79, pool:'apppool' },
  { uid:'n6', name:'aks-apppool-87654321-2', status:'Ready', roles:['worker'],        version:'v1.29.4', age:'45d', os:'Ubuntu 22.04', arch:'amd64', cpu:'8 cores', memory:'32Gi', cpuUsed:65, memUsed:68, pool:'apppool' },
  { uid:'n7', name:'aks-apppool-87654321-3', status:'Ready',    roles:['worker'],  version:'v1.29.4', age:'30d', os:'Ubuntu 22.04', arch:'amd64', cpu:'8 cores', memory:'32Gi', cpuUsed:12, memUsed:21, pool:'apppool' },
  { uid:'n8', name:'aks-dbpool-11223344-0',  status:'Ready', roles:['worker'],        version:'v1.29.4', age:'75d', os:'Ubuntu 22.04', arch:'amd64', cpu:'4 cores', memory:'16Gi', cpuUsed:26, memUsed:44, pool:'dbpool'  },
  { uid:'n9', name:'aks-dbpool-11223344-1',  status:'Ready', roles:['worker'],        version:'v1.29.4', age:'75d', os:'Ubuntu 22.04', arch:'amd64', cpu:'4 cores', memory:'16Gi', cpuUsed:22, memUsed:42, pool:'dbpool'  },
]

const NAMESPACES: KubeNamespace[] = [
  { uid:'ns1', name:'default',        status:'Active',      age:'85d', labels:{ 'kubernetes.io/metadata.name':'default' } },
  { uid:'ns2', name:'appcentral',     status:'Active',      age:'85d', labels:{ 'app.kubernetes.io/part-of':'aptean', 'tier':'app' } },
  { uid:'ns3', name:'aip',            status:'Active',      age:'80d', labels:{ 'app.kubernetes.io/part-of':'aptean', 'tier':'app' } },
  { uid:'ns4', name:'monitoring',     status:'Active',      age:'85d', labels:{ 'tier':'monitoring' } },
  { uid:'ns5', name:'ingress-nginx',  status:'Active',      age:'85d', labels:{ 'tier':'ingress' } },
  { uid:'ns6', name:'cert-manager',   status:'Active',      age:'84d', labels:{ 'tier':'cert' } },
  { uid:'ns7', name:'kube-system',    status:'Active',      age:'85d', labels:{ 'kubernetes.io/metadata.name':'kube-system' } },
  { uid:'ns8', name:'old-service',    status:'Terminating', age:'2d',  labels:{} },
]

const PODS: KubePod[] = [
  { uid:'p1',  name:'appcentral-api-7d4b9c-xkp2n',     namespace:'appcentral',    phase:'Running',           ready:'1/1', restarts:0,  node:'aks-apppool-87654321-0', ip:'10.244.3.14', containers:['api'],           image:'aptean/appcentral-api:2026.04', age:'12d', labels:{app:'appcentral', component:'api', version:'2026.04'} },
  { uid:'p2',  name:'appcentral-api-7d4b9c-lmn8p',     namespace:'appcentral',    phase:'Running',           ready:'1/1', restarts:0,  node:'aks-apppool-87654321-1', ip:'10.244.4.22', containers:['api'],           image:'aptean/appcentral-api:2026.04', age:'12d', labels:{app:'appcentral', component:'api', version:'2026.04'} },
  { uid:'p3',  name:'appcentral-worker-6c5d8b-rvt4k',  namespace:'appcentral',    phase:'Running',           ready:'1/1', restarts:1,  node:'aks-apppool-87654321-2', ip:'10.244.5.8',  containers:['worker'],        image:'aptean/appcentral-worker:2026.04', age:'12d', labels:{app:'appcentral', component:'worker'} },
  { uid:'p4',  name:'appcentral-ui-5f9e7a-wqp1x',      namespace:'appcentral',    phase:'Running',           ready:'1/1', restarts:0,  node:'aks-apppool-87654321-0', ip:'10.244.3.19', containers:['ui'],            image:'aptean/appcentral-ui:2026.04',  age:'12d', labels:{app:'appcentral', component:'ui'} },
  { uid:'p5',  name:'appcentral-db-migrate-7rz9p',      namespace:'appcentral',    phase:'Succeeded',         ready:'0/1', restarts:0,  node:'aks-dbpool-11223344-0',  ip:'10.244.7.3',  containers:['migrate'],       image:'aptean/appcentral-api:2026.04', age:'12d', labels:{app:'appcentral', component:'migrate', 'job-name':'db-migrate'} },
  { uid:'p6',  name:'aip-backend-8b4f2e-kzn7m',        namespace:'aip',           phase:'Running',           ready:'2/2', restarts:0,  node:'aks-apppool-87654321-1', ip:'10.244.4.31', containers:['api','sidecar'],  image:'aptean/aip-backend:2025.12', age:'33d', labels:{app:'aip', component:'backend'} },
  { uid:'p7',  name:'aip-scheduler-5d3c1b-pqr6t',      namespace:'aip',           phase:'CrashLoopBackOff',  ready:'0/1', restarts:14, node:'aks-apppool-87654321-2', ip:'10.244.5.11', containers:['scheduler'],     image:'aptean/aip-scheduler:2025.12', age:'33d', labels:{app:'aip', component:'scheduler'} },
  { uid:'p8',  name:'aip-frontend-6e7d4a-nxw5v',       namespace:'aip',           phase:'Running',           ready:'1/1', restarts:0,  node:'aks-apppool-87654321-0', ip:'10.244.3.27', containers:['frontend'],      image:'aptean/aip-frontend:2025.12',  age:'33d', labels:{app:'aip', component:'frontend'} },
  { uid:'p9',  name:'prometheus-0',                     namespace:'monitoring',    phase:'Running',           ready:'2/2', restarts:0,  node:'aks-apppool-87654321-1', ip:'10.244.4.44', containers:['prometheus','config-reloader'], image:'prom/prometheus:v2.49', age:'85d', labels:{app:'prometheus'} },
  { uid:'p10', name:'grafana-59d8f7-bzl3n',             namespace:'monitoring',    phase:'Running',           ready:'1/1', restarts:2,  node:'aks-apppool-87654321-2', ip:'10.244.5.21', containers:['grafana'],       image:'grafana/grafana:10.3.1', age:'85d', labels:{app:'grafana'} },
  { uid:'p11', name:'ingress-nginx-controller-rq9wx',   namespace:'ingress-nginx', phase:'Running',           ready:'1/1', restarts:0,  node:'aks-system-12345678-0',  ip:'10.244.1.5',  containers:['controller'],    image:'registry.k8s.io/ingress-nginx:v1.9.5', age:'85d', labels:{app:'ingress-nginx', component:'controller'} },
  { uid:'p12', name:'cert-manager-7b9c6d-fkl4s',       namespace:'cert-manager',  phase:'Running',           ready:'1/1', restarts:0,  node:'aks-system-12345678-1',  ip:'10.244.2.14', containers:['cert-manager'],  image:'quay.io/jetstack/cert-manager:v1.14', age:'84d', labels:{app:'cert-manager'} },
  { uid:'p13', name:'bad-pod-terminating-9xf2k',        namespace:'default',       phase:'Terminating',       ready:'0/1', restarts:3,  node:'aks-apppool-87654321-3', ip:'10.244.6.9',  containers:['app'],           image:'nginx:1.24', age:'1d', labels:{app:'test'} },
  { uid:'p14', name:'batch-job-report-20260414-hlp7q',  namespace:'appcentral',    phase:'Running',           ready:'1/1', restarts:0,  node:'aks-dbpool-11223344-1',  ip:'10.244.8.5',  containers:['report'],        image:'aptean/appcentral-api:2026.04', age:'1h', labels:{app:'appcentral', 'job-name':'report', component:'batch'} },
]

const DEPLOYMENTS: KubeDeployment[] = [
  { uid:'d1', name:'appcentral-api',     namespace:'appcentral',    ready:'2/2', upToDate:2, available:2, image:'aptean/appcentral-api:2026.04',    age:'12d', labels:{app:'appcentral', component:'api'} },
  { uid:'d2', name:'appcentral-worker',  namespace:'appcentral',    ready:'1/1', upToDate:1, available:1, image:'aptean/appcentral-worker:2026.04',  age:'12d', labels:{app:'appcentral', component:'worker'} },
  { uid:'d3', name:'appcentral-ui',      namespace:'appcentral',    ready:'1/1', upToDate:1, available:1, image:'aptean/appcentral-ui:2026.04',      age:'12d', labels:{app:'appcentral', component:'ui'} },
  { uid:'d4', name:'aip-backend',        namespace:'aip',           ready:'1/1', upToDate:1, available:1, image:'aptean/aip-backend:2025.12',        age:'33d', labels:{app:'aip', component:'backend'} },
  { uid:'d5', name:'aip-scheduler',      namespace:'aip',           ready:'0/1', upToDate:1, available:0, image:'aptean/aip-scheduler:2025.12',      age:'33d', labels:{app:'aip', component:'scheduler'} },
  { uid:'d6', name:'aip-frontend',       namespace:'aip',           ready:'1/1', upToDate:1, available:1, image:'aptean/aip-frontend:2025.12',       age:'33d', labels:{app:'aip', component:'frontend'} },
  { uid:'d7', name:'grafana',            namespace:'monitoring',    ready:'1/1', upToDate:1, available:1, image:'grafana/grafana:10.3.1',             age:'85d', labels:{app:'grafana'} },
  { uid:'d8', name:'cert-manager',       namespace:'cert-manager',  ready:'1/1', upToDate:1, available:1, image:'quay.io/jetstack/cert-manager:v1.14',age:'84d', labels:{app:'cert-manager'} },
]

const DAEMONSETS: KubeDaemonSet[] = [
  { uid:'ds1', name:'ingress-nginx-controller', namespace:'ingress-nginx', desired:3, current:3, ready:3, image:'registry.k8s.io/ingress-nginx:v1.9.5', age:'85d', labels:{app:'ingress-nginx'} },
  { uid:'ds2', name:'azure-cni-networkmonitor',  namespace:'kube-system',   desired:9, current:9, ready:9, image:'mcr.microsoft.com/containernetworking/networkmonitor:v1.4', age:'85d', labels:{component:'azure-cni'} },
  { uid:'ds3', name:'kube-proxy',                namespace:'kube-system',   desired:9, current:9, ready:9, image:'mcr.microsoft.com/oss/kubernetes/kube-proxy:v1.29.4', age:'85d', labels:{component:'kube-proxy'} },
  { uid:'ds4', name:'node-exporter',             namespace:'monitoring',    desired:9, current:9, ready:9, image:'prom/node-exporter:v1.7.0', age:'85d', labels:{app:'prometheus', component:'node-exporter'} },
]

const STATEFULSETS: KubeStatefulSet[] = [
  { uid:'ss1', name:'prometheus',  namespace:'monitoring', ready:'1/1', image:'prom/prometheus:v2.49',  age:'85d', labels:{app:'prometheus'} },
  { uid:'ss2', name:'alertmanager',namespace:'monitoring', ready:'1/1', image:'prom/alertmanager:v0.26', age:'85d', labels:{app:'alertmanager'} },
]

const JOBS: KubeJob[] = [
  { uid:'j1', name:'db-migrate-20260402',    namespace:'appcentral', completions:'1/1', duration:'2m14s', status:'Complete', age:'12d', labels:{'job-type':'migration', app:'appcentral'} },
  { uid:'j2', name:'report-20260414',        namespace:'appcentral', completions:'0/1', duration:'running', status:'Running', age:'1h', labels:{'job-type':'report', app:'appcentral'} },
  { uid:'j3', name:'seed-data-20260101',     namespace:'aip',        completions:'1/1', duration:'5m02s', status:'Complete', age:'103d', labels:{'job-type':'seed', app:'aip'} },
]

const CRONJOBS: KubeCronJob[] = [
  { uid:'cj1', name:'nightly-report',  namespace:'appcentral', schedule:'0 2 * * *',  lastRun:'12h ago',  active:0, suspended:false, age:'12d', labels:{app:'appcentral', component:'report'} },
  { uid:'cj2', name:'hourly-cleanup',  namespace:'appcentral', schedule:'0 * * * *',  lastRun:'45m ago',  active:0, suspended:false, age:'12d', labels:{app:'appcentral', component:'cleanup'} },
  { uid:'cj3', name:'weekly-backup',   namespace:'aip',        schedule:'0 3 * * 0',  lastRun:'2d ago',   active:0, suspended:false, age:'33d', labels:{app:'aip', component:'backup'} },
  { uid:'cj4', name:'metrics-export',  namespace:'monitoring', schedule:'*/15 * * * *',lastRun:'8m ago',  active:1, suspended:false, age:'60d', labels:{app:'monitoring'} },
]

const CONFIGMAPS: KubeConfigMap[] = [
  { uid:'cm1', name:'appcentral-config',      namespace:'appcentral',   keys:12, age:'12d', labels:{app:'appcentral'} },
  { uid:'cm2', name:'appcentral-env',         namespace:'appcentral',   keys:8,  age:'12d', labels:{app:'appcentral'} },
  { uid:'cm3', name:'aip-config',             namespace:'aip',          keys:9,  age:'33d', labels:{app:'aip'} },
  { uid:'cm4', name:'nginx-config',           namespace:'ingress-nginx',keys:3,  age:'85d', labels:{app:'ingress-nginx'} },
  { uid:'cm5', name:'prometheus-config',      namespace:'monitoring',   keys:2,  age:'85d', labels:{app:'prometheus'} },
  { uid:'cm6', name:'kube-dns',               namespace:'kube-system',  keys:1,  age:'85d', labels:{} },
  { uid:'cm7', name:'coredns',                namespace:'kube-system',  keys:1,  age:'85d', labels:{} },
]

const SECRETS: KubeSecret[] = [
  { uid:'sec1', name:'appcentral-db-secret',   namespace:'appcentral',   type:'Opaque',                keys:3, age:'12d', labels:{app:'appcentral', tier:'secret'} },
  { uid:'sec2', name:'appcentral-jwt-secret',  namespace:'appcentral',   type:'Opaque',                keys:1, age:'12d', labels:{app:'appcentral'} },
  { uid:'sec3', name:'appcentral-tls',         namespace:'appcentral',   type:'kubernetes.io/tls',     keys:2, age:'12d', labels:{app:'appcentral'} },
  { uid:'sec4', name:'aip-db-secret',          namespace:'aip',          type:'Opaque',                keys:2, age:'33d', labels:{app:'aip'} },
  { uid:'sec5', name:'regcred',                namespace:'appcentral',   type:'kubernetes.io/dockerconfigjson', keys:1, age:'85d', labels:{} },
  { uid:'sec6', name:'regcred',                namespace:'aip',          type:'kubernetes.io/dockerconfigjson', keys:1, age:'85d', labels:{} },
  { uid:'sec7', name:'cluster-tls-cert',       namespace:'ingress-nginx',type:'kubernetes.io/tls',     keys:2, age:'84d', labels:{} },
]

const HPAS: KubeHPA[] = [
  { uid:'hpa1', name:'appcentral-api-hpa',    namespace:'appcentral', ref:'Deployment/appcentral-api',    minPods:2, maxPods:10, currentPods:2, targetCPU:'70%', currentCPU:'61%', age:'12d', labels:{app:'appcentral'} },
  { uid:'hpa2', name:'aip-backend-hpa',       namespace:'aip',        ref:'Deployment/aip-backend',       minPods:1, maxPods:5,  currentPods:1, targetCPU:'80%', currentCPU:'44%', age:'33d', labels:{app:'aip'} },
]

const SERVICES: KubeService[] = [
  { uid:'svc1', name:'appcentral-api',     namespace:'appcentral',   type:'ClusterIP',     clusterIP:'10.0.147.23',  externalIP:'',               ports:'8000/TCP',       selector:{app:'appcentral', component:'api'},     age:'12d', labels:{app:'appcentral'} },
  { uid:'svc2', name:'appcentral-ui',      namespace:'appcentral',   type:'ClusterIP',     clusterIP:'10.0.201.88',  externalIP:'',               ports:'3000/TCP',       selector:{app:'appcentral', component:'ui'},      age:'12d', labels:{app:'appcentral'} },
  { uid:'svc3', name:'appcentral-lb',      namespace:'appcentral',   type:'LoadBalancer',  clusterIP:'10.0.112.44',  externalIP:'20.42.100.55',   ports:'80:31200/TCP,443:31201/TCP', selector:{app:'appcentral'}, age:'12d', labels:{app:'appcentral', tier:'frontend'} },
  { uid:'svc4', name:'aip-backend',        namespace:'aip',          type:'ClusterIP',     clusterIP:'10.0.99.77',   externalIP:'',               ports:'8080/TCP',       selector:{app:'aip', component:'backend'},       age:'33d', labels:{app:'aip'} },
  { uid:'svc5', name:'aip-lb',             namespace:'aip',          type:'LoadBalancer',  clusterIP:'10.0.55.12',   externalIP:'20.42.101.12',   ports:'80:30100/TCP,443:30101/TCP', selector:{app:'aip'},        age:'33d', labels:{app:'aip'} },
  { uid:'svc6', name:'prometheus',         namespace:'monitoring',   type:'ClusterIP',     clusterIP:'10.0.188.9',   externalIP:'',               ports:'9090/TCP',       selector:{app:'prometheus'},                     age:'85d', labels:{app:'prometheus'} },
  { uid:'svc7', name:'grafana',            namespace:'monitoring',   type:'NodePort',      clusterIP:'10.0.222.31',  externalIP:'',               ports:'3000:30300/TCP', selector:{app:'grafana'},                        age:'85d', labels:{app:'grafana'} },
  { uid:'svc8', name:'kubernetes',         namespace:'default',      type:'ClusterIP',     clusterIP:'10.0.0.1',     externalIP:'',               ports:'443/TCP',        selector:{},                                     age:'85d', labels:{} },
]

const INGRESSES: KubeIngress[] = [
  { uid:'ing1', name:'appcentral',   namespace:'appcentral',   className:'nginx', hosts:['appcentral.acme.aptean.app','www.appcentral.acme.aptean.app'], address:'20.42.100.55', age:'12d', labels:{app:'appcentral'} },
  { uid:'ing2', name:'aip',         namespace:'aip',          className:'nginx', hosts:['aip.acme.aptean.app'],                                         address:'20.42.101.12', age:'33d', labels:{app:'aip'} },
  { uid:'ing3', name:'grafana',     namespace:'monitoring',   className:'nginx', hosts:['grafana.internal.acme.aptean.app'],                            address:'20.42.100.55', age:'85d', labels:{app:'grafana'} },
]

const PVCS: KubePVC[] = [
  { uid:'pvc1', name:'appcentral-uploads',  namespace:'appcentral', status:'Bound',   storageClass:'managed-premium', capacity:'100Gi', accessMode:'ReadWriteOnce', age:'12d', labels:{app:'appcentral'} },
  { uid:'pvc2', name:'aip-data',           namespace:'aip',        status:'Bound',   storageClass:'managed-premium', capacity:'200Gi', accessMode:'ReadWriteOnce', age:'33d', labels:{app:'aip'} },
  { uid:'pvc3', name:'prometheus-data',    namespace:'monitoring', status:'Bound',   storageClass:'managed-premium', capacity:'50Gi',  accessMode:'ReadWriteOnce', age:'85d', labels:{app:'prometheus'} },
  { uid:'pvc4', name:'grafana-data',       namespace:'monitoring', status:'Bound',   storageClass:'managed-ssd',     capacity:'10Gi',  accessMode:'ReadWriteOnce', age:'85d', labels:{app:'grafana'} },
  { uid:'pvc5', name:'orphaned-claim',     namespace:'default',    status:'Lost',    storageClass:'managed-premium', capacity:'20Gi',  accessMode:'ReadWriteOnce', age:'5d',  labels:{} },
]

const PVS: KubePV[] = [
  { uid:'pv1', name:'pvc-appcentral-uploads', capacity:'100Gi', accessMode:'ReadWriteOnce', reclaim:'Delete', status:'Bound',   storageClass:'managed-premium', claim:'appcentral/appcentral-uploads', age:'12d' },
  { uid:'pv2', name:'pvc-aip-data',           capacity:'200Gi', accessMode:'ReadWriteOnce', reclaim:'Delete', status:'Bound',   storageClass:'managed-premium', claim:'aip/aip-data',                 age:'33d' },
  { uid:'pv3', name:'pvc-prometheus-data',    capacity:'50Gi',  accessMode:'ReadWriteOnce', reclaim:'Delete', status:'Bound',   storageClass:'managed-premium', claim:'monitoring/prometheus-data',   age:'85d' },
  { uid:'pv4', name:'pvc-grafana-data',       capacity:'10Gi',  accessMode:'ReadWriteOnce', reclaim:'Delete', status:'Bound',   storageClass:'managed-ssd',     claim:'monitoring/grafana-data',      age:'85d' },
  { uid:'pv5', name:'pvc-orphaned-claim',     capacity:'20Gi',  accessMode:'ReadWriteOnce', reclaim:'Retain', status:'Lost',    storageClass:'managed-premium', claim:'default/orphaned-claim',       age:'5d'  },
]

const STORAGE_CLASSES: KubeStorageClass[] = [
  { uid:'sc1', name:'managed-premium',    provisioner:'disk.csi.azure.com', reclaimPolicy:'Delete', volumeBinding:'WaitForFirstConsumer', default:true,  age:'85d' },
  { uid:'sc2', name:'managed-ssd',        provisioner:'disk.csi.azure.com', reclaimPolicy:'Delete', volumeBinding:'WaitForFirstConsumer', default:false, age:'85d' },
  { uid:'sc3', name:'azurefile',          provisioner:'file.csi.azure.com', reclaimPolicy:'Delete', volumeBinding:'Immediate',            default:false, age:'85d' },
  { uid:'sc4', name:'azurefile-premium',  provisioner:'file.csi.azure.com', reclaimPolicy:'Delete', volumeBinding:'Immediate',            default:false, age:'85d' },
]

const EVENTS: KubeEvent[] = [
  { uid:'ev1', namespace:'aip',       name:'aip-scheduler-5d3c1b',   reason:'BackOff',         message:'Back-off restarting failed container scheduler in pod aip-scheduler-5d3c1b-pqr6t', type:'Warning', count:152, age:'2h',  object:'Pod/aip-scheduler-5d3c1b-pqr6t' },
  { uid:'ev2', namespace:'aip',       name:'aip-scheduler-5d3c1b',   reason:'Failed',          message:'Error: ImagePullBackOff',                                                            type:'Warning', count:14,  age:'2h',  object:'Pod/aip-scheduler-5d3c1b-pqr6t' },
  { uid:'ev3', namespace:'appcentral',name:'appcentral-api-hpa',     reason:'SuccessfulRescale',message:'New size: 2; reason: All metrics below target',                                     type:'Normal',  count:3,   age:'4h',  object:'HorizontalPodAutoscaler/appcentral-api-hpa' },
  { uid:'ev4', namespace:'default',   name:'bad-pod-terminating',    reason:'Killing',         message:'Stopping container app',                                                             type:'Normal',  count:1,   age:'1h',  object:'Pod/bad-pod-terminating-9xf2k' },
  { uid:'ev5', namespace:'monitoring',name:'prometheus-0',           reason:'Pulled',          message:'Successfully pulled image "prom/prometheus:v2.49"',                                  type:'Normal',  count:1,   age:'85d', object:'Pod/prometheus-0' },
  { uid:'ev6', namespace:'kube-system',name:'kube-proxy',            reason:'NodeNotSchedulable',message:'Node aks-apppool-87654321-3 status is now: NodeNotSchedulable',                   type:'Warning', count:1,   age:'5d',  object:'Node/aks-apppool-87654321-3' },
]

const HELM_RELEASES: HelmRelease[] = [
  { uid:'hr1', name:'appcentral',     namespace:'appcentral',   chart:'appcentral',     chartVersion:'2026.4.0', appVersion:'2026.04', status:'deployed',        updated:'12d ago' },
  { uid:'hr2', name:'aip',            namespace:'aip',          chart:'aip',            chartVersion:'2025.12.1',appVersion:'2025.12', status:'deployed',        updated:'33d ago' },
  { uid:'hr3', name:'prometheus',     namespace:'monitoring',   chart:'kube-prometheus-stack', chartVersion:'56.3.0', appVersion:'v2.49', status:'deployed',     updated:'85d ago' },
  { uid:'hr4', name:'ingress-nginx',  namespace:'ingress-nginx',chart:'ingress-nginx',  chartVersion:'4.9.1',    appVersion:'1.9.5',   status:'deployed',        updated:'85d ago' },
  { uid:'hr5', name:'cert-manager',   namespace:'cert-manager', chart:'cert-manager',   chartVersion:'v1.14.3',  appVersion:'v1.14.3', status:'deployed',        updated:'84d ago' },
  { uid:'hr6', name:'aip-v2-canary',  namespace:'aip',          chart:'aip',            chartVersion:'2026.1.0-rc1', appVersion:'2026.01-rc', status:'failed',  updated:'2d ago' },
]

// ─── RBAC mock data ───────────────────────────────────────────────────────────

const ROLES: KubeRole[] = [
  { uid:'rol1', name:'appcentral-deployer',  namespace:'appcentral',  ruleCount:4, rules:[
    { verbs:['get','list','watch'],            apiGroups:['apps'],             resources:['deployments','replicasets'] },
    { verbs:['get','list','watch','create'],   apiGroups:[''],                 resources:['pods','pods/log'] },
    { verbs:['get','update','patch'],          apiGroups:['apps'],             resources:['deployments/scale'] },
    { verbs:['get','list'],                    apiGroups:[''],                 resources:['configmaps','secrets'] },
  ], age:'12d', labels:{app:'appcentral', 'managed-by':'ssp'} },
  { uid:'rol2', name:'aip-reader',           namespace:'aip',         ruleCount:2, rules:[
    { verbs:['get','list','watch'],            apiGroups:['apps',''],          resources:['deployments','pods','services'] },
    { verbs:['get','list'],                    apiGroups:[''],                 resources:['configmaps'] },
  ], age:'33d', labels:{app:'aip'} },
  { uid:'rol3', name:'monitoring-viewer',    namespace:'monitoring',  ruleCount:2, rules:[
    { verbs:['get','list','watch'],            apiGroups:[''],                 resources:['pods','services','endpoints'] },
    { verbs:['get','list'],                    apiGroups:['monitoring.coreos.com'], resources:['prometheusrules','servicemonitors'] },
  ], age:'85d', labels:{app:'prometheus'} },
  { uid:'rol4', name:'cert-manager-leaderelection', namespace:'cert-manager', ruleCount:1, rules:[
    { verbs:['get','update','patch','create'], apiGroups:['coordination.k8s.io'], resources:['leases'] },
  ], age:'84d', labels:{app:'cert-manager'} },
  { uid:'rol5', name:'ingress-nginx-admission', namespace:'ingress-nginx', ruleCount:3, rules:[
    { verbs:['get','create'],                  apiGroups:['admissionregistration.k8s.io'], resources:['validatingwebhookconfigurations'] },
    { verbs:['get','list','watch'],            apiGroups:[''],                 resources:['namespaces'] },
    { verbs:['create','get'],                  apiGroups:[''],                 resources:['events'] },
  ], age:'85d', labels:{app:'ingress-nginx'} },
]

const CLUSTER_ROLES: KubeClusterRole[] = [
  { uid:'cr1', name:'aptean-platform-admin',  ruleCount:6, aggregationRule:false, builtIn:false, rules:[
    { verbs:['*'],                             apiGroups:['apps',''],          resources:['deployments','services','pods','configmaps','secrets'] },
    { verbs:['get','list','watch'],            apiGroups:[''],                 resources:['nodes','namespaces','persistentvolumes'] },
    { verbs:['create','delete','patch'],       apiGroups:['rbac.authorization.k8s.io'], resources:['roles','rolebindings'] },
    { verbs:['get','list','watch'],            apiGroups:['networking.k8s.io'], resources:['ingresses','networkpolicies'] },
    { verbs:['get','list'],                    apiGroups:['storage.k8s.io'],   resources:['storageclasses','persistentvolumeclaims'] },
    { verbs:['get','list','watch','patch'],    apiGroups:['batch'],            resources:['jobs','cronjobs'] },
  ], age:'85d' },
  { uid:'cr2', name:'aptean-platform-viewer', ruleCount:3, aggregationRule:false, builtIn:false, rules:[
    { verbs:['get','list','watch'],            apiGroups:['apps',''],          resources:['deployments','services','pods','configmaps'] },
    { verbs:['get','list','watch'],            apiGroups:[''],                 resources:['nodes','namespaces'] },
    { verbs:['get','list','watch'],            apiGroups:['batch'],            resources:['jobs','cronjobs'] },
  ], age:'85d' },
  { uid:'cr3', name:'aptean-devops',          ruleCount:5, aggregationRule:false, builtIn:false, rules:[
    { verbs:['get','list','watch','create','update','patch','delete'], apiGroups:['apps'], resources:['deployments','statefulsets','daemonsets'] },
    { verbs:['get','list','watch','delete'],   apiGroups:[''],                 resources:['pods','pods/log','pods/exec'] },
    { verbs:['get','list','watch','create','update'], apiGroups:[''],         resources:['configmaps','secrets'] },
    { verbs:['get','list','watch'],            apiGroups:[''],                 resources:['nodes','namespaces','persistentvolumes'] },
    { verbs:['*'],                             apiGroups:['batch'],            resources:['jobs','cronjobs'] },
  ], age:'85d' },
  { uid:'cr4', name:'cluster-admin',          ruleCount:1, aggregationRule:false, builtIn:true, rules:[
    { verbs:['*'], apiGroups:['*'], resources:['*'] },
  ], age:'85d' },
  { uid:'cr5', name:'view',                   ruleCount:1, aggregationRule:true,  builtIn:true, rules:[
    { verbs:['get','list','watch'],            apiGroups:['*'],                resources:['*'] },
  ], age:'85d' },
  { uid:'cr6', name:'edit',                   ruleCount:1, aggregationRule:true,  builtIn:true, rules:[
    { verbs:['get','list','watch','create','update','patch','delete'], apiGroups:['*'], resources:['*'] },
  ], age:'85d' },
]

const ROLE_BINDINGS: KubeRoleBinding[] = [
  { uid:'rb1', name:'appcentral-deployer-binding',  namespace:'appcentral', roleRef:'appcentral-deployer', roleKind:'Role', subjects:[
    { kind:'ServiceAccount', name:'appcentral-sa', namespace:'appcentral' },
    { kind:'Group',          name:'aptean:devops' },
  ], age:'12d', labels:{app:'appcentral'} },
  { uid:'rb2', name:'aip-reader-binding',           namespace:'aip',        roleRef:'aip-reader',          roleKind:'Role', subjects:[
    { kind:'ServiceAccount', name:'aip-sa',           namespace:'aip' },
    { kind:'User',           name:'alice@aptean.com' },
  ], age:'33d', labels:{app:'aip'} },
  { uid:'rb3', name:'monitoring-viewer-binding',    namespace:'monitoring', roleRef:'monitoring-viewer',   roleKind:'Role', subjects:[
    { kind:'ServiceAccount', name:'grafana',          namespace:'monitoring' },
  ], age:'85d', labels:{app:'monitoring'} },
  { uid:'rb4', name:'devops-team-appcentral',       namespace:'appcentral', roleRef:'aptean-devops',       roleKind:'ClusterRole', subjects:[
    { kind:'Group', name:'aptean:devops' },
  ], age:'12d', labels:{'managed-by':'ssp'} },
  { uid:'rb5', name:'devops-team-aip',              namespace:'aip',        roleRef:'aptean-devops',       roleKind:'ClusterRole', subjects:[
    { kind:'Group', name:'aptean:devops' },
  ], age:'33d', labels:{'managed-by':'ssp'} },
]

const CLUSTER_ROLE_BINDINGS: KubeClusterRoleBinding[] = [
  { uid:'crb1', name:'aptean-platform-admins', roleRef:'aptean-platform-admin', builtIn:false, subjects:[
    { kind:'Group', name:'aptean:platform-admins' },
    { kind:'User',  name:'platform-service@aptean.com' },
  ], age:'85d' },
  { uid:'crb2', name:'aptean-platform-viewers', roleRef:'aptean-platform-viewer', builtIn:false, subjects:[
    { kind:'Group', name:'aptean:support' },
    { kind:'Group', name:'aptean:operations' },
  ], age:'85d' },
  { uid:'crb3', name:'cluster-admin-binding',   roleRef:'cluster-admin', builtIn:false, subjects:[
    { kind:'User', name:'admin@aptean.com' },
  ], age:'85d' },
  { uid:'crb4', name:'system:coredns',          roleRef:'system:coredns', builtIn:true, subjects:[
    { kind:'ServiceAccount', name:'coredns', namespace:'kube-system' },
  ], age:'85d' },
  { uid:'crb5', name:'system:node',             roleRef:'system:node', builtIn:true, subjects:[
    { kind:'Group', name:'system:nodes' },
  ], age:'85d' },
]

const SERVICE_ACCOUNTS: KubeServiceAccount[] = [
  { uid:'sa1', name:'appcentral-sa',    namespace:'appcentral',   secrets:2, automountToken:true,  age:'12d', labels:{app:'appcentral', 'managed-by':'ssp'} },
  { uid:'sa2', name:'appcentral-worker',namespace:'appcentral',   secrets:2, automountToken:true,  age:'12d', labels:{app:'appcentral', component:'worker'} },
  { uid:'sa3', name:'aip-sa',           namespace:'aip',          secrets:2, automountToken:true,  age:'33d', labels:{app:'aip', 'managed-by':'ssp'} },
  { uid:'sa4', name:'grafana',          namespace:'monitoring',   secrets:1, automountToken:true,  age:'85d', labels:{app:'grafana'} },
  { uid:'sa5', name:'prometheus',       namespace:'monitoring',   secrets:1, automountToken:false, age:'85d', labels:{app:'prometheus'} },
  { uid:'sa6', name:'cert-manager',     namespace:'cert-manager', secrets:1, automountToken:true,  age:'84d', labels:{app:'cert-manager'} },
  { uid:'sa7', name:'ingress-nginx',    namespace:'ingress-nginx',secrets:1, automountToken:true,  age:'85d', labels:{app:'ingress-nginx'} },
  { uid:'sa8', name:'default',          namespace:'appcentral',   secrets:0, automountToken:false, age:'12d', labels:{} },
  { uid:'sa9', name:'default',          namespace:'aip',          secrets:0, automountToken:false, age:'33d', labels:{} },
]

// ─── Mock logs ────────────────────────────────────────────────────────────────

function mockLogs(podName: string): string[] {
  const ts = () => new Date(Date.now() - Math.random() * 60000).toISOString()
  return [
    `${ts()} INFO  Starting pod ${podName}`,
    `${ts()} INFO  Connecting to database...`,
    `${ts()} INFO  Database connection established`,
    `${ts()} INFO  Loading configuration from ConfigMap`,
    `${ts()} INFO  Server listening on :8000`,
    `${ts()} INFO  GET /api/v1/health → 200 (3ms)`,
    `${ts()} INFO  POST /api/v1/deployments/ → 201 (44ms)`,
    `${ts()} DEBUG Celery beat running`,
    `${ts()} INFO  GET /api/v1/products/ → 200 (8ms)`,
    `${ts()} INFO  Cache hit for tenant-acme-001`,
    `${ts()} WARN  Slow query detected: 320ms`,
    `${ts()} INFO  GET /api/v1/deployments/requests/ → 200 (12ms)`,
    `${ts()} INFO  Task triggered: trigger_deployment(42)`,
    `${ts()} INFO  Deployment 42 status → validating`,
    `${ts()} INFO  Deployment 42 status → provisioning_infra`,
  ]
}

// ─── Mock YAML ────────────────────────────────────────────────────────────────

function mockYaml(kind: string, name: string, namespace: string): string {
  return `apiVersion: apps/v1
kind: ${kind}
metadata:
  name: ${name}
  namespace: ${namespace}
  labels:
    app: ${name.split('-')[0]}
    managed-by: aptean-ssp
  annotations:
    deployment.kubernetes.io/revision: "3"
spec:
  replicas: 2
  selector:
    matchLabels:
      app: ${name.split('-')[0]}
  template:
    metadata:
      labels:
        app: ${name.split('-')[0]}
    spec:
      containers:
      - name: ${name.split('-')[0]}
        image: aptean/${name.split('-')[0]}:2026.04
        ports:
        - containerPort: 8000
        resources:
          requests:
            cpu: 250m
            memory: 512Mi
          limits:
            cpu: 1000m
            memory: 2Gi
        envFrom:
        - configMapRef:
            name: ${name.split('-')[0]}-config
        - secretRef:
            name: ${name.split('-')[0]}-db-secret
status:
  replicas: 2
  readyReplicas: 2
  updatedReplicas: 2
  availableReplicas: 2`
}

// ─── Nav config ───────────────────────────────────────────────────────────────

interface NavItem { kind: ResourceKind; label: string; count?: number }
interface NavGroup { id: string; label: string; icon: React.ElementType; items: NavItem[] }

const NAV_GROUPS: NavGroup[] = [
  {
    id: 'cluster', label: 'Cluster', icon: Server,
    items: [
      { kind: 'overview',    label: 'Overview' },
      { kind: 'nodes',       label: 'Nodes',      count: NODES.length },
      { kind: 'namespaces',  label: 'Namespaces', count: NAMESPACES.length },
      { kind: 'events',      label: 'Events',     count: EVENTS.length },
    ],
  },
  {
    id: 'workloads', label: 'Workloads', icon: Box,
    items: [
      { kind: 'pods',         label: 'Pods',         count: PODS.length },
      { kind: 'deployments',  label: 'Deployments',  count: DEPLOYMENTS.length },
      { kind: 'daemonsets',   label: 'DaemonSets',   count: DAEMONSETS.length },
      { kind: 'statefulsets', label: 'StatefulSets', count: STATEFULSETS.length },
      { kind: 'replicasets',  label: 'ReplicaSets',  count: 8 },
      { kind: 'jobs',         label: 'Jobs',         count: JOBS.length },
      { kind: 'cronjobs',     label: 'CronJobs',     count: CRONJOBS.length },
    ],
  },
  {
    id: 'config', label: 'Config', icon: Settings,
    items: [
      { kind: 'configmaps',     label: 'ConfigMaps',      count: CONFIGMAPS.length },
      { kind: 'secrets',        label: 'Secrets',         count: SECRETS.length },
      { kind: 'hpas',           label: 'HPAs',            count: HPAS.length },
      { kind: 'resourcequotas', label: 'Resource Quotas', count: 3 },
    ],
  },
  {
    id: 'network', label: 'Network', icon: Network,
    items: [
      { kind: 'services',  label: 'Services',  count: SERVICES.length },
      { kind: 'endpoints', label: 'Endpoints', count: SERVICES.length },
      { kind: 'ingresses', label: 'Ingresses', count: INGRESSES.length },
    ],
  },
  {
    id: 'storage', label: 'Storage', icon: Database,
    items: [
      { kind: 'pvcs',          label: 'Persistent Volume Claims', count: PVCS.length },
      { kind: 'pvs',           label: 'Persistent Volumes',      count: PVS.length },
      { kind: 'storageclasses',label: 'Storage Classes',         count: STORAGE_CLASSES.length },
    ],
  },
  {
    id: 'helm', label: 'Helm', icon: GitBranch,
    items: [
      { kind: 'releases', label: 'Releases', count: HELM_RELEASES.length },
    ],
  },
  {
    id: 'rbac', label: 'RBAC', icon: Shield,
    items: [
      { kind: 'roles',              label: 'Roles',                  count: ROLES.length },
      { kind: 'clusterroles',       label: 'Cluster Roles',          count: CLUSTER_ROLES.length },
      { kind: 'rolebindings',       label: 'Role Bindings',          count: ROLE_BINDINGS.length },
      { kind: 'clusterrolebindings',label: 'Cluster Role Bindings',  count: CLUSTER_ROLE_BINDINGS.length },
      { kind: 'serviceaccounts',    label: 'Service Accounts',       count: SERVICE_ACCOUNTS.length },
    ],
  },
]

// ─── Helpers ──────────────────────────────────────────────────────────────────

function phaseColor(phase: PodPhase) {
  if (phase === 'Running')          return 'var(--color-status-resolved)'
  if (phase === 'Succeeded')        return 'var(--color-teal-400)'
  if (phase === 'CrashLoopBackOff') return 'var(--color-severity-critical)'
  if (phase === 'Failed')           return 'var(--color-severity-critical)'
  if (phase === 'Terminating')      return 'var(--color-severity-high)'
  if (phase === 'Pending')          return 'var(--color-severity-medium)'
  return 'var(--color-gray-500)'
}

function helmStatusColor(s: HelmStatus) {
  if (s === 'deployed')        return 'var(--color-status-resolved)'
  if (s === 'failed')          return 'var(--color-severity-critical)'
  if (s === 'pending-upgrade') return 'var(--color-severity-high)'
  return 'var(--color-gray-500)'
}

function pvcStatusColor(s: PVCState) {
  if (s === 'Bound')   return 'var(--color-status-resolved)'
  if (s === 'Lost')    return 'var(--color-severity-critical)'
  return 'var(--color-severity-high)'
}

function UtilBar({ value }: { value: number }) {
  const c = value >= 90 ? 'var(--color-severity-critical)' : value >= 70 ? 'var(--color-severity-high)' : 'var(--color-status-resolved)'
  return (
    <div className={styles.utilTrack}>
      <div className={styles.utilFill} style={{ width: `${value}%`, background: c }} />
      <span className={styles.utilPct}>{value}%</span>
    </div>
  )
}

function LabelBadges({ labels }: { labels: Record<string, string> }) {
  const entries = Object.entries(labels).slice(0, 3)
  return (
    <div className={styles.labelBadges}>
      {entries.map(([k, v]) => (
        <span key={k} className={styles.labelBadge}>{k}={v}</span>
      ))}
      {Object.keys(labels).length > 3 && (
        <span className={styles.labelMore}>+{Object.keys(labels).length - 3}</span>
      )}
    </div>
  )
}

// ─── Detail panel ─────────────────────────────────────────────────────────────

type DetailTab = 'info' | 'yaml' | 'logs' | 'shell'

function DetailPanel({
  kind, resource, onClose,
}: {
  kind: ResourceKind
  resource: KubeResource & Record<string, unknown>
  onClose: () => void
}) {
  const [tab, setTab]     = useState<DetailTab>('info')
  const [logs]            = useState<string[]>(() => mockLogs(resource.name as string))
  const [shellLines, setShellLines] = useState<string[]>(['$ '])
  const [shellInput, setShellInput] = useState('')
  const logEndRef  = useRef<HTMLDivElement>(null)
  const inputRef   = useRef<HTMLInputElement>(null)
  const isPod      = kind === 'pods'
  const yaml       = mockYaml(kind === 'deployments' ? 'Deployment' : kind === 'pods' ? 'Pod' : 'Resource', resource.name as string, resource.namespace as string)

  useEffect(() => { logEndRef.current?.scrollIntoView({ behavior: 'smooth' }) }, [logs])

  function handleShellKey(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key !== 'Enter') return
    const cmd = shellInput.trim()
    const responses: Record<string, string> = {
      'ls':         'app  bin  etc  lib  proc  run  tmp  usr  var',
      'ls /app':    'manage.py  pyproject.toml  ssp_api  requirements.txt',
      'pwd':        '/app',
      'env | grep DATABASE': 'DATABASE_URL=postgresql://postgres:***@db:5432/ssp',
      'ps aux':     'PID  CMD\n  1  python manage.py runserver\n 14  celery worker',
      'cat /etc/hostname': resource.name as string,
      'whoami':     'app',
      'date':       new Date().toUTCString(),
    }
    const out = responses[cmd] ?? (cmd ? `bash: ${cmd}: command not found` : '')
    setShellLines(prev => [
      ...prev.slice(0, -1),
      `$ ${cmd}`,
      ...(out ? [out] : []),
      '$ ',
    ])
    setShellInput('')
    setTimeout(() => inputRef.current?.focus(), 10)
  }

  const tabs: Array<{ id: DetailTab; label: string; icon: React.ElementType; only?: boolean }> = [
    { id: 'info',  label: 'Info',     icon: Info    },
    { id: 'yaml',  label: 'YAML',     icon: Code    },
    { id: 'logs',  label: 'Logs',     icon: List,   only: isPod },
    { id: 'shell', label: 'Shell',    icon: Terminal, only: isPod },
  ]

  return (
    <div className={styles.detailPanel}>
      <div className={styles.detailHeader}>
        <div className={styles.detailTitle}>
          <span className={styles.detailKind}>{kind.toUpperCase()}</span>
          <span className={styles.detailName}>{resource.name as string}</span>
        </div>
        <div className={styles.detailActions}>
          <button className={styles.iconBtn} title="Copy name" onClick={() => navigator.clipboard?.writeText(resource.name as string)}><Copy size={13} /></button>
          <button className={styles.iconBtn} title="Close" onClick={onClose}><X size={13} /></button>
        </div>
      </div>

      <div className={styles.detailTabBar}>
        {tabs.filter(t => t.only !== false && (t.only === undefined || t.only)).map(t => (
          <button key={t.id} className={clsx(styles.detailTab, tab === t.id && styles.detailTabActive)} onClick={() => setTab(t.id)}>
            <t.icon size={12} />{t.label}
          </button>
        ))}
      </div>

      <div className={styles.detailBody}>
        {/* Info tab */}
        {tab === 'info' && (
          <div className={styles.infoGrid}>
            <div className={styles.infoSection}>
              <div className={styles.infoSectionTitle}>Metadata</div>
              <InfoRow label="Name"      value={resource.name as string} mono />
              <InfoRow label="Namespace" value={resource.namespace} />
              <InfoRow label="Age"       value={resource.age as string} />
              {'node' in resource && <InfoRow label="Node" value={resource.node as string} mono />}
              {'ip' in resource && <InfoRow label="Pod IP" value={resource.ip as string} mono />}
              {'clusterIP' in resource && <InfoRow label="Cluster IP" value={resource.clusterIP as string} mono />}
              {'type' in resource && kind === 'services' && <InfoRow label="Type" value={resource.type as string} />}
              {'phase' in resource && <InfoRow label="Phase" value={resource.phase as string} />}
              {'ready' in resource && <InfoRow label="Ready" value={resource.ready as string} />}
              {'restarts' in resource && <InfoRow label="Restarts" value={String(resource.restarts)} />}
              {'image' in resource && <InfoRow label="Image" value={resource.image as string} mono />}
              {'capacity' in resource && kind === 'pvcs' && <InfoRow label="Capacity" value={resource.capacity as string} />}
              {'storageClass' in resource && <InfoRow label="Storage Class" value={resource.storageClass as string} />}
            </div>
            <div className={styles.infoSection}>
              <div className={styles.infoSectionTitle}>Labels</div>
              {Object.entries(resource.labels as Record<string, string>).length > 0 ? (
                Object.entries(resource.labels as Record<string, string>).map(([k, v]) => (
                  <div key={k} className={styles.labelRow}>
                    <Tag size={10} className={styles.labelRowIcon} />
                    <span className={styles.labelKey}>{k}</span>
                    <span className={styles.labelVal}>=</span>
                    <span className={styles.labelVal}>{v}</span>
                  </div>
                ))
              ) : <span className={styles.infoEmpty}>No labels</span>}
            </div>
          </div>
        )}

        {/* YAML tab */}
        {tab === 'yaml' && (
          <div className={styles.yamlWrap}>
            <div className={styles.yamlToolbar}>
              <span className={styles.yamlTitle}>Resource YAML</span>
              <button className={styles.iconBtn} onClick={() => navigator.clipboard?.writeText(yaml)}><Copy size={12} /> Copy</button>
            </div>
            <pre className={styles.yamlPre}><code>{yaml}</code></pre>
          </div>
        )}

        {/* Logs tab */}
        {tab === 'logs' && isPod && (
          <div className={styles.logsWrap}>
            <div className={styles.logsToolbar}>
              <span className={styles.logsTitle}><List size={12} /> stdout</span>
              <button className={styles.iconBtn}><RefreshCw size={12} /> Refresh</button>
            </div>
            <div className={styles.logsPre}>
              {logs.map((line, i) => {
                const lvl = line.includes('ERROR') ? 'error' : line.includes('WARN') ? 'warn' : line.includes('DEBUG') ? 'debug' : ''
                return (
                  <div key={i} className={clsx(styles.logLine, lvl && styles[`logLine_${lvl}`])}>
                    {line}
                  </div>
                )
              })}
              <div ref={logEndRef} />
            </div>
          </div>
        )}

        {/* Shell tab */}
        {tab === 'shell' && isPod && (
          <div className={styles.shellWrap}>
            <div className={styles.shellToolbar}>
              <Terminal size={12} />
              <span>{resource.name as string}</span>
            </div>
            <div className={styles.shellBody} onClick={() => inputRef.current?.focus()}>
              {shellLines.slice(0, -1).map((line, i) => (
                <div key={i} className={styles.shellLine}>{line}</div>
              ))}
              <div className={styles.shellInputRow}>
                <span className={styles.shellPrompt}>$ </span>
                <input
                  ref={inputRef}
                  className={styles.shellInput}
                  value={shellInput}
                  onChange={e => setShellInput(e.target.value)}
                  onKeyDown={handleShellKey}
                  autoFocus
                  spellCheck={false}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function InfoRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className={styles.infoRow}>
      <span className={styles.infoLabel}>{label}</span>
      <span className={clsx(styles.infoValue, mono && styles.mono)}>{value}</span>
    </div>
  )
}

// ─── Resource tables ──────────────────────────────────────────────────────────

function PodTable({ pods, ns, search, onSelect }: { pods: KubePod[]; ns: string; search: string; onSelect: (r: KubeResource & Record<string, unknown>) => void }) {
  const filtered = pods.filter(p =>
    (ns === 'all' || p.namespace === ns) &&
    (!search || p.name.includes(search) || p.namespace.includes(search))
  )
  return (
    <table className={styles.resTable}>
      <thead><tr>
        <th>Name</th><th>Namespace</th><th>Ready</th><th>Status</th><th>Restarts</th><th>Node</th><th>Age</th><th>Labels</th>
      </tr></thead>
      <tbody>{filtered.map(p => (
        <tr key={p.uid} className={styles.resRow} onClick={() => onSelect(p as unknown as KubeResource & Record<string, unknown>)}>
          <td className={styles.nameCell}>{p.name}</td>
          <td><span className={styles.nsBadge}>{p.namespace}</span></td>
          <td>{p.ready}</td>
          <td><span className={styles.statusDot} style={{ background: phaseColor(p.phase) }} />{p.phase}</td>
          <td className={p.restarts > 5 ? styles.textWarn : ''}>{p.restarts}</td>
          <td className={styles.mono}>{p.node.replace('aks-','').slice(0,20)}</td>
          <td className={styles.ageCell}>{p.age}</td>
          <td><LabelBadges labels={p.labels} /></td>
        </tr>
      ))}</tbody>
    </table>
  )
}

function DeployTable({ items, ns, search, onSelect }: { items: KubeDeployment[]; ns: string; search: string; onSelect: (r: KubeResource & Record<string, unknown>) => void }) {
  const filtered = items.filter(d => (ns === 'all' || d.namespace === ns) && (!search || d.name.includes(search)))
  return (
    <table className={styles.resTable}>
      <thead><tr><th>Name</th><th>Namespace</th><th>Ready</th><th>Up-to-date</th><th>Available</th><th>Age</th><th>Image</th></tr></thead>
      <tbody>{filtered.map(d => {
        const [r, t] = d.ready.split('/').map(Number)
        const ok = r === t
        return (
          <tr key={d.uid} className={styles.resRow} onClick={() => onSelect(d as unknown as KubeResource & Record<string, unknown>)}>
            <td className={styles.nameCell}>{d.name}</td>
            <td><span className={styles.nsBadge}>{d.namespace}</span></td>
            <td><span style={{ color: ok ? 'var(--color-status-resolved)' : 'var(--color-severity-critical)' }}>{d.ready}</span></td>
            <td>{d.upToDate}</td>
            <td>{d.available}</td>
            <td className={styles.ageCell}>{d.age}</td>
            <td className={styles.mono} style={{ fontSize: 11 }}>{d.image}</td>
          </tr>
        )
      })}</tbody>
    </table>
  )
}

function DaemonTable({ items, ns, search, onSelect }: { items: KubeDaemonSet[]; ns: string; search: string; onSelect: (r: KubeResource & Record<string, unknown>) => void }) {
  const filtered = items.filter(d => (ns === 'all' || d.namespace === ns) && (!search || d.name.includes(search)))
  return (
    <table className={styles.resTable}>
      <thead><tr><th>Name</th><th>Namespace</th><th>Desired</th><th>Current</th><th>Ready</th><th>Age</th><th>Image</th></tr></thead>
      <tbody>{filtered.map(d => (
        <tr key={d.uid} className={styles.resRow} onClick={() => onSelect(d as unknown as KubeResource & Record<string, unknown>)}>
          <td className={styles.nameCell}>{d.name}</td>
          <td><span className={styles.nsBadge}>{d.namespace}</span></td>
          <td>{d.desired}</td>
          <td>{d.current}</td>
          <td style={{ color: d.ready === d.desired ? 'var(--color-status-resolved)' : 'var(--color-severity-critical)' }}>{d.ready}</td>
          <td className={styles.ageCell}>{d.age}</td>
          <td className={styles.mono} style={{ fontSize: 11 }}>{d.image}</td>
        </tr>
      ))}</tbody>
    </table>
  )
}

function StatefulTable({ items, ns, search, onSelect }: { items: KubeStatefulSet[]; ns: string; search: string; onSelect: (r: KubeResource & Record<string, unknown>) => void }) {
  const filtered = items.filter(d => (ns === 'all' || d.namespace === ns) && (!search || d.name.includes(search)))
  return (
    <table className={styles.resTable}>
      <thead><tr><th>Name</th><th>Namespace</th><th>Ready</th><th>Age</th><th>Image</th></tr></thead>
      <tbody>{filtered.map(d => (
        <tr key={d.uid} className={styles.resRow} onClick={() => onSelect(d as unknown as KubeResource & Record<string, unknown>)}>
          <td className={styles.nameCell}>{d.name}</td>
          <td><span className={styles.nsBadge}>{d.namespace}</span></td>
          <td>{d.ready}</td>
          <td className={styles.ageCell}>{d.age}</td>
          <td className={styles.mono} style={{ fontSize: 11 }}>{d.image}</td>
        </tr>
      ))}</tbody>
    </table>
  )
}

function JobTable({ items, ns, search, onSelect }: { items: KubeJob[]; ns: string; search: string; onSelect: (r: KubeResource & Record<string, unknown>) => void }) {
  const filtered = items.filter(d => (ns === 'all' || d.namespace === ns) && (!search || d.name.includes(search)))
  return (
    <table className={styles.resTable}>
      <thead><tr><th>Name</th><th>Namespace</th><th>Completions</th><th>Duration</th><th>Status</th><th>Age</th></tr></thead>
      <tbody>{filtered.map(j => (
        <tr key={j.uid} className={styles.resRow} onClick={() => onSelect(j as unknown as KubeResource & Record<string, unknown>)}>
          <td className={styles.nameCell}>{j.name}</td>
          <td><span className={styles.nsBadge}>{j.namespace}</span></td>
          <td>{j.completions}</td>
          <td>{j.duration}</td>
          <td><span className={styles.statusDot} style={{ background: j.status === 'Complete' ? 'var(--color-status-resolved)' : j.status === 'Failed' ? 'var(--color-severity-critical)' : 'var(--color-severity-high)' }} />{j.status}</td>
          <td className={styles.ageCell}>{j.age}</td>
        </tr>
      ))}</tbody>
    </table>
  )
}

function CronJobTable({ items, ns, search, onSelect }: { items: KubeCronJob[]; ns: string; search: string; onSelect: (r: KubeResource & Record<string, unknown>) => void }) {
  const filtered = items.filter(d => (ns === 'all' || d.namespace === ns) && (!search || d.name.includes(search)))
  return (
    <table className={styles.resTable}>
      <thead><tr><th>Name</th><th>Namespace</th><th>Schedule</th><th>Last Run</th><th>Active</th><th>Suspended</th><th>Age</th></tr></thead>
      <tbody>{filtered.map(c => (
        <tr key={c.uid} className={styles.resRow} onClick={() => onSelect(c as unknown as KubeResource & Record<string, unknown>)}>
          <td className={styles.nameCell}>{c.name}</td>
          <td><span className={styles.nsBadge}>{c.namespace}</span></td>
          <td className={styles.mono}>{c.schedule}</td>
          <td>{c.lastRun}</td>
          <td>{c.active}</td>
          <td>{c.suspended ? <span style={{ color: 'var(--color-severity-high)' }}>Yes</span> : 'No'}</td>
          <td className={styles.ageCell}>{c.age}</td>
        </tr>
      ))}</tbody>
    </table>
  )
}

function NodeTable({ onSelect }: { onSelect: (r: KubeResource & Record<string, unknown>) => void }) {
  return (
    <table className={styles.resTable}>
      <thead><tr><th>Name</th><th>Status</th><th>Roles</th><th>Version</th><th>CPU</th><th>Memory</th><th>Age</th><th>OS</th></tr></thead>
      <tbody>{NODES.map(n => (
        <tr key={n.uid} className={styles.resRow} onClick={() => onSelect({ ...n, namespace: '', labels: {} } as unknown as KubeResource & Record<string, unknown>)}>
          <td className={styles.nameCell}>{n.name}</td>
          <td><span className={styles.statusDot} style={{ background: n.status === 'Ready' ? 'var(--color-status-resolved)' : 'var(--color-severity-critical)' }} />{n.status}</td>
          <td>{n.roles.join(',')}</td>
          <td className={styles.mono}>{n.version}</td>
          <td style={{ minWidth: 110 }}><UtilBar value={n.cpuUsed} /></td>
          <td style={{ minWidth: 110 }}><UtilBar value={n.memUsed} /></td>
          <td className={styles.ageCell}>{n.age}</td>
          <td style={{ fontSize: 11, color: 'var(--color-gray-500)' }}>{n.os}</td>
        </tr>
      ))}</tbody>
    </table>
  )
}

function NamespaceTable({ onSelect }: { onSelect: (r: KubeResource & Record<string, unknown>) => void }) {
  return (
    <table className={styles.resTable}>
      <thead><tr><th>Name</th><th>Status</th><th>Age</th><th>Labels</th></tr></thead>
      <tbody>{NAMESPACES.map(ns => (
        <tr key={ns.uid} className={styles.resRow} onClick={() => onSelect(ns as unknown as KubeResource & Record<string, unknown>)}>
          <td className={styles.nameCell}>{ns.name}</td>
          <td><span className={styles.statusDot} style={{ background: ns.status === 'Active' ? 'var(--color-status-resolved)' : 'var(--color-severity-high)' }} />{ns.status}</td>
          <td className={styles.ageCell}>{ns.age}</td>
          <td><LabelBadges labels={ns.labels} /></td>
        </tr>
      ))}</tbody>
    </table>
  )
}

function EventTable({ ns, search }: { ns: string; search: string }) {
  const filtered = EVENTS.filter(e => (ns === 'all' || e.namespace === ns) && (!search || e.message.includes(search) || e.reason.includes(search)))
  return (
    <table className={styles.resTable}>
      <thead><tr><th>Type</th><th>Object</th><th>Reason</th><th>Message</th><th>Count</th><th>Age</th></tr></thead>
      <tbody>{filtered.map(ev => (
        <tr key={ev.uid} className={styles.resRow}>
          <td>
            {ev.type === 'Warning'
              ? <span style={{ display:'flex', alignItems:'center', gap:4, color:'var(--color-severity-high)' }}><AlertTriangle size={12} /> Warning</span>
              : <span style={{ display:'flex', alignItems:'center', gap:4, color:'var(--color-gray-500)' }}><Info size={12} /> Normal</span>
            }
          </td>
          <td className={styles.mono} style={{ fontSize: 11 }}>{ev.object}</td>
          <td><span className={styles.reasonBadge}>{ev.reason}</span></td>
          <td style={{ maxWidth: 340, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ev.message}</td>
          <td>{ev.count}</td>
          <td className={styles.ageCell}>{ev.age}</td>
        </tr>
      ))}</tbody>
    </table>
  )
}

function ConfigMapTable({ items, ns, search, onSelect }: { items: KubeConfigMap[]; ns: string; search: string; onSelect: (r: KubeResource & Record<string, unknown>) => void }) {
  const filtered = items.filter(d => (ns === 'all' || d.namespace === ns) && (!search || d.name.includes(search)))
  return (
    <table className={styles.resTable}>
      <thead><tr><th>Name</th><th>Namespace</th><th>Keys</th><th>Age</th><th>Labels</th></tr></thead>
      <tbody>{filtered.map(c => (
        <tr key={c.uid} className={styles.resRow} onClick={() => onSelect(c as unknown as KubeResource & Record<string, unknown>)}>
          <td className={styles.nameCell}>{c.name}</td>
          <td><span className={styles.nsBadge}>{c.namespace}</span></td>
          <td>{c.keys}</td>
          <td className={styles.ageCell}>{c.age}</td>
          <td><LabelBadges labels={c.labels} /></td>
        </tr>
      ))}</tbody>
    </table>
  )
}

function SecretTable({ items, ns, search, onSelect }: { items: KubeSecret[]; ns: string; search: string; onSelect: (r: KubeResource & Record<string, unknown>) => void }) {
  const filtered = items.filter(d => (ns === 'all' || d.namespace === ns) && (!search || d.name.includes(search)))
  return (
    <table className={styles.resTable}>
      <thead><tr><th>Name</th><th>Namespace</th><th>Type</th><th>Keys</th><th>Age</th></tr></thead>
      <tbody>{filtered.map(s => (
        <tr key={s.uid} className={styles.resRow} onClick={() => onSelect(s as unknown as KubeResource & Record<string, unknown>)}>
          <td className={styles.nameCell}>{s.name}</td>
          <td><span className={styles.nsBadge}>{s.namespace}</span></td>
          <td className={styles.mono} style={{ fontSize: 11 }}>{s.type}</td>
          <td>{s.keys}</td>
          <td className={styles.ageCell}>{s.age}</td>
        </tr>
      ))}</tbody>
    </table>
  )
}

function HPATable({ items, ns, search, onSelect }: { items: KubeHPA[]; ns: string; search: string; onSelect: (r: KubeResource & Record<string, unknown>) => void }) {
  const filtered = items.filter(d => (ns === 'all' || d.namespace === ns) && (!search || d.name.includes(search)))
  return (
    <table className={styles.resTable}>
      <thead><tr><th>Name</th><th>Namespace</th><th>Reference</th><th>Min</th><th>Max</th><th>Replicas</th><th>CPU Target</th><th>CPU Current</th><th>Age</th></tr></thead>
      <tbody>{filtered.map(h => (
        <tr key={h.uid} className={styles.resRow} onClick={() => onSelect(h as unknown as KubeResource & Record<string, unknown>)}>
          <td className={styles.nameCell}>{h.name}</td>
          <td><span className={styles.nsBadge}>{h.namespace}</span></td>
          <td className={styles.mono} style={{ fontSize: 11 }}>{h.ref}</td>
          <td>{h.minPods}</td>
          <td>{h.maxPods}</td>
          <td>{h.currentPods}</td>
          <td>{h.targetCPU}</td>
          <td>{h.currentCPU}</td>
          <td className={styles.ageCell}>{h.age}</td>
        </tr>
      ))}</tbody>
    </table>
  )
}

function ServiceTable({ items, ns, search, onSelect }: { items: KubeService[]; ns: string; search: string; onSelect: (r: KubeResource & Record<string, unknown>) => void }) {
  const filtered = items.filter(d => (ns === 'all' || d.namespace === ns) && (!search || d.name.includes(search)))
  return (
    <table className={styles.resTable}>
      <thead><tr><th>Name</th><th>Namespace</th><th>Type</th><th>Cluster IP</th><th>External IP</th><th>Ports</th><th>Age</th></tr></thead>
      <tbody>{filtered.map(s => (
        <tr key={s.uid} className={styles.resRow} onClick={() => onSelect(s as unknown as KubeResource & Record<string, unknown>)}>
          <td className={styles.nameCell}>{s.name}</td>
          <td><span className={styles.nsBadge}>{s.namespace}</span></td>
          <td>
            <span className={clsx(styles.svcTypeBadge, s.type === 'LoadBalancer' && styles.svcTypeLB, s.type === 'NodePort' && styles.svcTypeNP)}>
              {s.type}
            </span>
          </td>
          <td className={styles.mono}>{s.clusterIP}</td>
          <td className={styles.mono}>{s.externalIP || '—'}</td>
          <td className={styles.mono} style={{ fontSize: 11 }}>{s.ports}</td>
          <td className={styles.ageCell}>{s.age}</td>
        </tr>
      ))}</tbody>
    </table>
  )
}

function IngressTable({ items, ns, search, onSelect }: { items: KubeIngress[]; ns: string; search: string; onSelect: (r: KubeResource & Record<string, unknown>) => void }) {
  const filtered = items.filter(d => (ns === 'all' || d.namespace === ns) && (!search || d.name.includes(search)))
  return (
    <table className={styles.resTable}>
      <thead><tr><th>Name</th><th>Namespace</th><th>Class</th><th>Hosts</th><th>Address</th><th>Age</th></tr></thead>
      <tbody>{filtered.map(i => (
        <tr key={i.uid} className={styles.resRow} onClick={() => onSelect(i as unknown as KubeResource & Record<string, unknown>)}>
          <td className={styles.nameCell}>{i.name}</td>
          <td><span className={styles.nsBadge}>{i.namespace}</span></td>
          <td>{i.className}</td>
          <td className={styles.mono} style={{ fontSize: 11 }}>{i.hosts.join(', ')}</td>
          <td className={styles.mono}>{i.address}</td>
          <td className={styles.ageCell}>{i.age}</td>
        </tr>
      ))}</tbody>
    </table>
  )
}

function PVCTable({ items, ns, search, onSelect }: { items: KubePVC[]; ns: string; search: string; onSelect: (r: KubeResource & Record<string, unknown>) => void }) {
  const filtered = items.filter(d => (ns === 'all' || d.namespace === ns) && (!search || d.name.includes(search)))
  return (
    <table className={styles.resTable}>
      <thead><tr><th>Name</th><th>Namespace</th><th>Status</th><th>Capacity</th><th>Access Mode</th><th>Storage Class</th><th>Age</th></tr></thead>
      <tbody>{filtered.map(p => (
        <tr key={p.uid} className={styles.resRow} onClick={() => onSelect(p as unknown as KubeResource & Record<string, unknown>)}>
          <td className={styles.nameCell}>{p.name}</td>
          <td><span className={styles.nsBadge}>{p.namespace}</span></td>
          <td><span className={styles.statusDot} style={{ background: pvcStatusColor(p.status) }} />{p.status}</td>
          <td>{p.capacity}</td>
          <td>{p.accessMode}</td>
          <td>{p.storageClass}</td>
          <td className={styles.ageCell}>{p.age}</td>
        </tr>
      ))}</tbody>
    </table>
  )
}

function PVTable({ search, onSelect }: { search: string; onSelect: (r: KubeResource & Record<string, unknown>) => void }) {
  const filtered = PVS.filter(p => !search || p.name.includes(search))
  return (
    <table className={styles.resTable}>
      <thead><tr><th>Name</th><th>Capacity</th><th>Access Mode</th><th>Reclaim</th><th>Status</th><th>Claim</th><th>Storage Class</th><th>Age</th></tr></thead>
      <tbody>{filtered.map(p => (
        <tr key={p.uid} className={styles.resRow} onClick={() => onSelect({ ...p, namespace: '', labels: {} } as unknown as KubeResource & Record<string, unknown>)}>
          <td className={styles.nameCell}>{p.name}</td>
          <td>{p.capacity}</td>
          <td>{p.accessMode}</td>
          <td>{p.reclaim}</td>
          <td><span className={styles.statusDot} style={{ background: pvcStatusColor(p.status) }} />{p.status}</td>
          <td className={styles.mono} style={{ fontSize: 11 }}>{p.claim}</td>
          <td>{p.storageClass}</td>
          <td className={styles.ageCell}>{p.age}</td>
        </tr>
      ))}</tbody>
    </table>
  )
}

function StorageClassTable({ search }: { search: string }) {
  const filtered = STORAGE_CLASSES.filter(s => !search || s.name.includes(search))
  return (
    <table className={styles.resTable}>
      <thead><tr><th>Name</th><th>Provisioner</th><th>Reclaim Policy</th><th>Volume Binding</th><th>Default</th><th>Age</th></tr></thead>
      <tbody>{filtered.map(s => (
        <tr key={s.uid} className={styles.resRow}>
          <td className={styles.nameCell}>{s.name}{s.default && <span className={styles.defaultBadge}>default</span>}</td>
          <td className={styles.mono} style={{ fontSize: 11 }}>{s.provisioner}</td>
          <td>{s.reclaimPolicy}</td>
          <td>{s.volumeBinding}</td>
          <td>{s.default ? '✓' : ''}</td>
          <td className={styles.ageCell}>{s.age}</td>
        </tr>
      ))}</tbody>
    </table>
  )
}

function HelmTable({ search, onSelect }: { search: string; onSelect: (r: KubeResource & Record<string, unknown>) => void }) {
  const filtered = HELM_RELEASES.filter(r => !search || r.name.includes(search))
  return (
    <table className={styles.resTable}>
      <thead><tr><th>Name</th><th>Namespace</th><th>Chart</th><th>Chart Version</th><th>App Version</th><th>Status</th><th>Updated</th></tr></thead>
      <tbody>{filtered.map(r => (
        <tr key={r.uid} className={styles.resRow} onClick={() => onSelect({ uid: r.uid, name: r.name, namespace: r.namespace, age: r.updated, labels: {} } as unknown as KubeResource & Record<string, unknown>)}>
          <td className={styles.nameCell}>{r.name}</td>
          <td><span className={styles.nsBadge}>{r.namespace}</span></td>
          <td className={styles.mono}>{r.chart}</td>
          <td className={styles.mono}>{r.chartVersion}</td>
          <td className={styles.mono}>{r.appVersion}</td>
          <td><span className={styles.statusDot} style={{ background: helmStatusColor(r.status) }} />{r.status}</td>
          <td className={styles.ageCell}>{r.updated}</td>
        </tr>
      ))}</tbody>
    </table>
  )
}

// ─── RBAC tables ──────────────────────────────────────────────────────────────

function RulesCell({ rules }: { rules: PolicyRule[] }) {
  const summary = rules.slice(0, 2).map(r =>
    `${r.verbs.join(',')} → ${r.resources.join(',')}`
  )
  return (
    <div className={styles.rulesList}>
      {summary.map((s, i) => <div key={i} className={styles.ruleItem}>{s}</div>)}
      {rules.length > 2 && <div className={styles.rulesMore}>+{rules.length - 2} more</div>}
    </div>
  )
}

function SubjectsCell({ subjects }: { subjects: Array<{ kind: string; name: string; namespace?: string }> }) {
  return (
    <div className={styles.subjectsList}>
      {subjects.map((s, i) => (
        <span key={i} className={clsx(
          styles.subjectBadge,
          s.kind === 'User'           && styles.subjectUser,
          s.kind === 'Group'          && styles.subjectGroup,
          s.kind === 'ServiceAccount' && styles.subjectSA,
        )}>
          {s.kind === 'User'           && <span title="User"><Users size={9} /></span>}
          {s.kind === 'Group'          && <span title="Group"><Shield size={9} /></span>}
          {s.kind === 'ServiceAccount' && <span title="ServiceAccount"><UserCheck size={9} /></span>}
          {s.name}
        </span>
      ))}
    </div>
  )
}

function RoleTable({ items, ns, search, onSelect }: { items: KubeRole[]; ns: string; search: string; onSelect: (r: KubeResource & Record<string, unknown>) => void }) {
  const filtered = items.filter(r => (ns === 'all' || r.namespace === ns) && (!search || r.name.includes(search)))
  return (
    <table className={styles.resTable}>
      <thead><tr><th>Name</th><th>Namespace</th><th>Rules</th><th>Policy Preview</th><th>Age</th></tr></thead>
      <tbody>{filtered.map(r => (
        <tr key={r.uid} className={styles.resRow} onClick={() => onSelect(r as unknown as KubeResource & Record<string, unknown>)}>
          <td className={styles.nameCell}><Key size={12} style={{ marginRight: 5, color: 'var(--color-teal-500)', verticalAlign: 'middle' }} />{r.name}</td>
          <td><span className={styles.nsBadge}>{r.namespace}</span></td>
          <td><span className={styles.rbacRuleCount}>{r.ruleCount}</span></td>
          <td><RulesCell rules={r.rules} /></td>
          <td className={styles.ageCell}>{r.age}</td>
        </tr>
      ))}</tbody>
    </table>
  )
}

function ClusterRoleTable({ search, onSelect }: { search: string; onSelect: (r: KubeResource & Record<string, unknown>) => void }) {
  const filtered = CLUSTER_ROLES.filter(r => !search || r.name.includes(search))
  return (
    <table className={styles.resTable}>
      <thead><tr><th>Name</th><th>Rules</th><th>Aggregation</th><th>Built-in</th><th>Policy Preview</th><th>Age</th></tr></thead>
      <tbody>{filtered.map(r => (
        <tr key={r.uid} className={styles.resRow} onClick={() => onSelect({ ...r, namespace: '', labels: {} } as unknown as KubeResource & Record<string, unknown>)}>
          <td className={styles.nameCell}><Shield size={12} style={{ marginRight: 5, color: 'var(--color-teal-500)', verticalAlign: 'middle' }} />{r.name}{r.builtIn && <span className={styles.builtInBadge}>built-in</span>}</td>
          <td><span className={styles.rbacRuleCount}>{r.ruleCount}</span></td>
          <td>{r.aggregationRule ? <span style={{ color: 'var(--color-teal-500)', fontWeight: 600 }}>Yes</span> : '—'}</td>
          <td>{r.builtIn ? <span style={{ color: 'var(--color-gray-400)' }}>Yes</span> : <span style={{ color: 'var(--color-teal-500)', fontWeight: 600 }}>No</span>}</td>
          <td><RulesCell rules={r.rules} /></td>
          <td className={styles.ageCell}>{r.age}</td>
        </tr>
      ))}</tbody>
    </table>
  )
}

function RoleBindingTable({ items, ns, search, onSelect }: { items: KubeRoleBinding[]; ns: string; search: string; onSelect: (r: KubeResource & Record<string, unknown>) => void }) {
  const filtered = items.filter(r => (ns === 'all' || r.namespace === ns) && (!search || r.name.includes(search)))
  return (
    <table className={styles.resTable}>
      <thead><tr><th>Name</th><th>Namespace</th><th>Role Ref</th><th>Kind</th><th>Subjects</th><th>Age</th></tr></thead>
      <tbody>{filtered.map(r => (
        <tr key={r.uid} className={styles.resRow} onClick={() => onSelect(r as unknown as KubeResource & Record<string, unknown>)}>
          <td className={styles.nameCell}>{r.name}</td>
          <td><span className={styles.nsBadge}>{r.namespace}</span></td>
          <td className={styles.mono} style={{ fontSize: 11, color: 'var(--color-teal-600)' }}>{r.roleRef}</td>
          <td><span className={clsx(styles.roleKindBadge, r.roleKind === 'ClusterRole' && styles.roleKindCluster)}>{r.roleKind}</span></td>
          <td><SubjectsCell subjects={r.subjects} /></td>
          <td className={styles.ageCell}>{r.age}</td>
        </tr>
      ))}</tbody>
    </table>
  )
}

function ClusterRoleBindingTable({ search, onSelect }: { search: string; onSelect: (r: KubeResource & Record<string, unknown>) => void }) {
  const filtered = CLUSTER_ROLE_BINDINGS.filter(r => !search || r.name.includes(search))
  return (
    <table className={styles.resTable}>
      <thead><tr><th>Name</th><th>Cluster Role Ref</th><th>Built-in</th><th>Subjects</th><th>Age</th></tr></thead>
      <tbody>{filtered.map(r => (
        <tr key={r.uid} className={styles.resRow} onClick={() => onSelect({ uid: r.uid, name: r.name, namespace: '', age: r.age, labels: {} } as unknown as KubeResource & Record<string, unknown>)}>
          <td className={styles.nameCell}>{r.name}{r.builtIn && <span className={styles.builtInBadge}>built-in</span>}</td>
          <td className={styles.mono} style={{ fontSize: 11, color: 'var(--color-teal-600)' }}>{r.roleRef}</td>
          <td>{r.builtIn ? <span style={{ color: 'var(--color-gray-400)' }}>Yes</span> : <span style={{ color: 'var(--color-teal-500)', fontWeight: 600 }}>No</span>}</td>
          <td><SubjectsCell subjects={r.subjects} /></td>
          <td className={styles.ageCell}>{r.age}</td>
        </tr>
      ))}</tbody>
    </table>
  )
}

function ServiceAccountTable({ items, ns, search, onSelect }: { items: KubeServiceAccount[]; ns: string; search: string; onSelect: (r: KubeResource & Record<string, unknown>) => void }) {
  const filtered = items.filter(s => (ns === 'all' || s.namespace === ns) && (!search || s.name.includes(search)))
  return (
    <table className={styles.resTable}>
      <thead><tr><th>Name</th><th>Namespace</th><th>Secrets</th><th>Automount Token</th><th>Labels</th><th>Age</th></tr></thead>
      <tbody>{filtered.map(s => (
        <tr key={s.uid} className={styles.resRow} onClick={() => onSelect(s as unknown as KubeResource & Record<string, unknown>)}>
          <td className={styles.nameCell}><UserCheck size={12} style={{ marginRight: 5, color: 'var(--color-gray-400)', verticalAlign: 'middle' }} />{s.name}</td>
          <td><span className={styles.nsBadge}>{s.namespace}</span></td>
          <td>{s.secrets}</td>
          <td>
            {s.automountToken
              ? <span style={{ color: 'var(--color-severity-high)', fontWeight: 600, fontSize: 11 }}>Enabled</span>
              : <span style={{ color: 'var(--color-status-resolved)', fontWeight: 600, fontSize: 11 }}>Disabled</span>
            }
          </td>
          <td><LabelBadges labels={s.labels} /></td>
          <td className={styles.ageCell}>{s.age}</td>
        </tr>
      ))}</tbody>
    </table>
  )
}

// ─── Overview ─────────────────────────────────────────────────────────────────

function ClusterOverview({ cluster }: { cluster: AKSCluster }) {
  const running   = PODS.filter(p => p.phase === 'Running').length
  const crashed   = PODS.filter(p => p.phase === 'CrashLoopBackOff' || p.phase === 'Failed').length
  const warnings  = EVENTS.filter(e => e.type === 'Warning').length
  const readyNodes = NODES.filter(n => n.status === 'Ready').length

  return (
    <div className={styles.overview}>
      <div className={styles.overviewTitle}>{cluster.name}</div>
      <div className={styles.overviewMeta}>
        <span><Globe size={12}/> {cluster.region}</span>
        <span><Layers size={12}/> k8s {cluster.k8sVersion}</span>
        <span><HardDrive size={12}/> {cluster.resourceGroup}</span>
        <span><Calendar size={12}/> Created {cluster.createdAt}</span>
        <span className={styles.mono} style={{ fontSize: 11 }}>{cluster.apiServerUrl}</span>
      </div>

      <div className={styles.overviewKpis}>
        {[
          { label: 'Nodes',       value: `${readyNodes}/${NODES.length}`, color: 'var(--color-status-resolved)', icon: Server },
          { label: 'Pods',        value: `${running}/${PODS.length}`,     color: 'var(--color-status-resolved)', icon: Box },
          { label: 'Crashloops',  value: String(crashed),                 color: crashed ? 'var(--color-severity-critical)' : 'var(--color-gray-500)', icon: AlertCircle },
          { label: 'Deployments', value: String(DEPLOYMENTS.length),      color: 'var(--color-teal-500)', icon: Zap },
          { label: 'Services',    value: String(SERVICES.length),         color: 'var(--color-teal-500)', icon: Globe },
          { label: 'Warnings',    value: String(warnings),                color: warnings ? 'var(--color-severity-high)' : 'var(--color-gray-500)', icon: AlertTriangle },
          { label: 'Helm Releases',value: String(HELM_RELEASES.length),  color: 'var(--color-teal-500)', icon: GitBranch },
          { label: 'Namespaces',  value: String(NAMESPACES.length),       color: 'var(--color-teal-500)', icon: Layers },
        ].map(({ label, value, color, icon: Icon }) => (
          <div key={label} className={styles.overviewKpi}>
            <div className={styles.overviewKpiIcon} style={{ color }}>
              <Icon size={16} />
            </div>
            <div className={styles.overviewKpiValue} style={{ color }}>{value}</div>
            <div className={styles.overviewKpiLabel}>{label}</div>
          </div>
        ))}
      </div>

      <div className={styles.overviewNodes}>
        <div className={styles.overviewSubtitle}>Node Utilisation</div>
        <table className={styles.resTable}>
          <thead><tr><th>Node</th><th>Pool</th><th>Status</th><th>CPU</th><th>Memory</th><th>Version</th><th>Age</th></tr></thead>
          <tbody>{NODES.map(n => (
            <tr key={n.uid} className={styles.resRow}>
              <td className={styles.nameCell}>{n.name}</td>
              <td><span className={styles.nsBadge}>{n.pool}</span></td>
              <td><span className={styles.statusDot} style={{ background: n.status === 'Ready' ? 'var(--color-status-resolved)' : 'var(--color-severity-critical)' }} />{n.status}</td>
              <td style={{ minWidth: 120 }}><UtilBar value={n.cpuUsed} /></td>
              <td style={{ minWidth: 120 }}><UtilBar value={n.memUsed} /></td>
              <td className={styles.mono}>{n.version}</td>
              <td className={styles.ageCell}>{n.age}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </div>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export function AKSManagementPage() {
  const [selectedCluster, setSelectedCluster] = useState<AKSCluster>(CLUSTERS[0])
  const [activeKind, setActiveKind]           = useState<ResourceKind>('overview')
  const [namespace, setNamespace]             = useState('all')
  const [search, setSearch]                   = useState('')
  const [selectedResource, setSelectedResource] = useState<(KubeResource & Record<string, unknown>) | null>(null)
  const [openGroups, setOpenGroups]           = useState<Set<string>>(new Set(['cluster', 'workloads', 'config', 'network', 'storage', 'helm', 'rbac']))
  const [refreshing, setRefreshing]           = useState(false)

  function toggleGroup(id: string) {
    setOpenGroups(prev => {
      const next = new Set(prev)
      if (next.has(id)) { next.delete(id) } else { next.add(id) }
      return next
    })
  }

  function handleRefresh() {
    setRefreshing(true)
    setTimeout(() => setRefreshing(false), 900)
  }

  function selectKind(kind: ResourceKind) {
    setActiveKind(kind)
    setSelectedResource(null)
    setSearch('')
  }

  const allNs = ['all', ...NAMESPACES.map(n => n.name)]
  const kindLabel = NAV_GROUPS.flatMap(g => g.items).find(i => i.kind === activeKind)?.label ?? activeKind

  const crashCount = PODS.filter(p => p.phase === 'CrashLoopBackOff').length
  const warnCount  = EVENTS.filter(e => e.type === 'Warning').length

  return (
    <div className={styles.page}>
      {/* ── Top bar ── */}
      <div className={styles.topBar}>
        <div className={styles.topBarLeft}>
          <div className={styles.clusterSelector}>
            <Server size={14} />
            <select
              className={styles.clusterSelect}
              value={selectedCluster.id}
              onChange={e => setSelectedCluster(CLUSTERS.find(c => c.id === e.target.value) ?? CLUSTERS[0])}
            >
              {CLUSTERS.map(c => (
                <option key={c.id} value={c.id}>
                  {c.name} ({c.environment})
                </option>
              ))}
            </select>
            <span className={styles.clusterStatusDot} style={{
              background: selectedCluster.status === 'Running' ? 'var(--color-status-resolved)'
                : selectedCluster.status === 'Upgrading' ? 'var(--color-severity-high)'
                : 'var(--color-gray-500)'
            }} />
          </div>
          <span className={styles.topBarMeta}>{selectedCluster.region} · k8s {selectedCluster.k8sVersion}</span>
          {crashCount > 0 && <span className={styles.topBarAlert}><AlertCircle size={11} /> {crashCount} CrashLoop</span>}
          {warnCount  > 0 && <span className={styles.topBarWarn}><AlertTriangle size={11} /> {warnCount} Warnings</span>}
        </div>
        <div className={styles.topBarRight}>
          <button className={styles.tbBtn} title="Refresh" onClick={handleRefresh}>
            <RefreshCw size={13} className={refreshing ? styles.spin : undefined} />
          </button>
          <button className={styles.tbBtn} title="kubectl terminal"><Terminal size={13} /></button>
          <button className={styles.tbBtn} title="External link"><ExternalLink size={13} /></button>
        </div>
      </div>

      <div className={styles.workspace}>
        {/* ── Left nav ── */}
        <nav className={styles.lensNav}>
          {NAV_GROUPS.map(group => (
            <div key={group.id} className={styles.navGroup}>
              <button
                className={styles.navGroupHeader}
                onClick={() => toggleGroup(group.id)}
              >
                <group.icon size={13} className={styles.navGroupIcon} />
                <span>{group.label}</span>
                {openGroups.has(group.id) ? <ChevronDown size={11} className={styles.navChevron} /> : <ChevronRight size={11} className={styles.navChevron} />}
              </button>
              {openGroups.has(group.id) && (
                <div className={styles.navItems}>
                  {group.items.map(item => (
                    <button
                      key={item.kind}
                      className={clsx(styles.navItem, activeKind === item.kind && styles.navItemActive)}
                      onClick={() => selectKind(item.kind)}
                    >
                      <span className={styles.navItemLabel}>{item.label}</span>
                      {item.count !== undefined && (
                        <span className={clsx(styles.navCount, activeKind === item.kind && styles.navCountActive)}>
                          {item.count}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>

        {/* ── Main content ── */}
        <div className={styles.mainArea}>
          {/* Toolbar */}
          {activeKind !== 'overview' && (
            <div className={styles.resToolbar}>
              <span className={styles.resTitle}>{kindLabel}</span>
              {activeKind !== 'nodes' && activeKind !== 'pvs' && activeKind !== 'storageclasses' && activeKind !== 'events' && activeKind !== 'releases' && activeKind !== 'namespaces' && activeKind !== 'clusterroles' && activeKind !== 'clusterrolebindings' && (
                <select
                  className={styles.nsSelect}
                  value={namespace}
                  onChange={e => setNamespace(e.target.value)}
                >
                  {allNs.map(ns => (
                    <option key={ns} value={ns}>{ns === 'all' ? 'All namespaces' : ns}</option>
                  ))}
                </select>
              )}
              <div className={styles.searchBox}>
                <Search size={12} className={styles.searchIcon} />
                <input
                  className={styles.searchInput}
                  placeholder={`Filter ${kindLabel.toLowerCase()}…`}
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
                {search && <button className={styles.clearSearch} onClick={() => setSearch('')}><X size={11} /></button>}
              </div>
            </div>
          )}

          {/* Resource content */}
          <div className={styles.resContent}>
            {activeKind === 'overview'     && <ClusterOverview cluster={selectedCluster} />}
            {activeKind === 'nodes'        && <NodeTable onSelect={setSelectedResource} />}
            {activeKind === 'namespaces'   && <NamespaceTable onSelect={setSelectedResource} />}
            {activeKind === 'events'       && <EventTable ns={namespace} search={search} />}
            {activeKind === 'pods'         && <PodTable pods={PODS} ns={namespace} search={search} onSelect={setSelectedResource} />}
            {activeKind === 'deployments'  && <DeployTable items={DEPLOYMENTS} ns={namespace} search={search} onSelect={setSelectedResource} />}
            {activeKind === 'daemonsets'   && <DaemonTable items={DAEMONSETS} ns={namespace} search={search} onSelect={setSelectedResource} />}
            {activeKind === 'statefulsets' && <StatefulTable items={STATEFULSETS} ns={namespace} search={search} onSelect={setSelectedResource} />}
            {activeKind === 'replicasets'  && <DeployTable items={DEPLOYMENTS} ns={namespace} search={search} onSelect={setSelectedResource} />}
            {activeKind === 'jobs'         && <JobTable items={JOBS} ns={namespace} search={search} onSelect={setSelectedResource} />}
            {activeKind === 'cronjobs'     && <CronJobTable items={CRONJOBS} ns={namespace} search={search} onSelect={setSelectedResource} />}
            {activeKind === 'configmaps'   && <ConfigMapTable items={CONFIGMAPS} ns={namespace} search={search} onSelect={setSelectedResource} />}
            {activeKind === 'secrets'      && <SecretTable items={SECRETS} ns={namespace} search={search} onSelect={setSelectedResource} />}
            {activeKind === 'hpas'         && <HPATable items={HPAS} ns={namespace} search={search} onSelect={setSelectedResource} />}
            {activeKind === 'resourcequotas' && <ConfigMapTable items={CONFIGMAPS.slice(0,3)} ns={namespace} search={search} onSelect={setSelectedResource} />}
            {activeKind === 'services'     && <ServiceTable items={SERVICES} ns={namespace} search={search} onSelect={setSelectedResource} />}
            {activeKind === 'endpoints'    && <ServiceTable items={SERVICES} ns={namespace} search={search} onSelect={setSelectedResource} />}
            {activeKind === 'ingresses'    && <IngressTable items={INGRESSES} ns={namespace} search={search} onSelect={setSelectedResource} />}
            {activeKind === 'pvcs'         && <PVCTable items={PVCS} ns={namespace} search={search} onSelect={setSelectedResource} />}
            {activeKind === 'pvs'          && <PVTable search={search} onSelect={setSelectedResource} />}
            {activeKind === 'storageclasses'      && <StorageClassTable search={search} />}
            {activeKind === 'releases'            && <HelmTable search={search} onSelect={setSelectedResource} />}
            {activeKind === 'roles'               && <RoleTable items={ROLES} ns={namespace} search={search} onSelect={setSelectedResource} />}
            {activeKind === 'clusterroles'        && <ClusterRoleTable search={search} onSelect={setSelectedResource} />}
            {activeKind === 'rolebindings'        && <RoleBindingTable items={ROLE_BINDINGS} ns={namespace} search={search} onSelect={setSelectedResource} />}
            {activeKind === 'clusterrolebindings' && <ClusterRoleBindingTable search={search} onSelect={setSelectedResource} />}
            {activeKind === 'serviceaccounts'     && <ServiceAccountTable items={SERVICE_ACCOUNTS} ns={namespace} search={search} onSelect={setSelectedResource} />}
          </div>
        </div>

        {/* ── Detail panel ── */}
        {selectedResource && (
          <DetailPanel
            kind={activeKind}
            resource={selectedResource}
            onClose={() => setSelectedResource(null)}
          />
        )}
      </div>
    </div>
  )
}
